import { useMemo, useEffect, useRef } from 'react';
import { normalizeLifecycleStatus, buildRegionStatusSummary } from '../../../utils/queryListUtils';

/**
 * Hook to manage query filtering, pagination, and region summary for AllQueries page
 * @param {Object} params - Hook parameters
 * @param {Array} params.allQueries - All queries array
 * @param {string} params.selectedRegionCard - Selected region card
 * @param {string} params.selectedStatusCard - Selected status card
 * @param {number} params.page - Current page
 * @param {number} params.rowsPerPage - Rows per page
 * @param {Function} params.setQueries - Setter for queries
 * @param {Function} params.setTotal - Setter for total
 * @param {Function} params.setRegionStatusSummary - Setter for region status summary
 * @param {Function} params.setPage - Setter for page
 * @returns {Object} Filtered and paginated data
 */
export const useAllQueriesPageFilters = ({
  allQueries,
  selectedRegionCard,
  selectedStatusCard,
  page,
  rowsPerPage,
  backendCounts,
  setQueries,
  setTotal,
  setRegionStatusSummary,
  setPage,
}) => {
  // Filter queries by region and/or status card
  const filteredQueries = useMemo(() => {
    let filtered = allQueries;

    // Only filter by region card selection if something is selected
    if (selectedRegionCard) {
      filtered = filtered.filter((query) => {
        const queryRegion = (query.region || '').trim();
        return queryRegion === selectedRegionCard;
      });
    }

    // Then filter by status card selection (works with region filter)
    if (selectedStatusCard) {
      filtered = filtered.filter((query) => {
        const normalized = normalizeLifecycleStatus(query.statusLabel || query.status);
        const statusMap = {
          'Raised': 'raised',
          'Open': 'raised',
          'Delegated': 'delegated',
          'Escalated': 'escalated',
          'Closed': 'closed',
        };
        const targetStatus = statusMap[selectedStatusCard];
        if (selectedStatusCard === 'Raised' || selectedStatusCard === 'Open') {
          return normalized === 'raised' || normalized === 'open';
        }
        return normalized === targetStatus;
      });
    }

    return filtered;
  }, [allQueries, selectedRegionCard, selectedStatusCard]);

  // Apply client-side pagination to filtered queries
  const paginatedQueries = useMemo(() => {
    const startIndex = page * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return filteredQueries.slice(startIndex, endIndex);
  }, [filteredQueries, page, rowsPerPage]);

  // Update displayed queries and region summary when data changes
  useEffect(() => {
    setQueries(paginatedQueries);
    setTotal(filteredQueries.length);

    // ── Region status summary: backend counts for status totals, plus a
    // client-side per-region unique-project count (allQueries, not
    // filteredQueries, so each region card always reflects its full project
    // count regardless of the currently selected region/status card) ───────
    const regionSummary = buildRegionStatusSummary(backendCounts, allQueries);
    setRegionStatusSummary(regionSummary);
    // ────────────────────────────────────────────────────────────────────────
  }, [allQueries, backendCounts, paginatedQueries, filteredQueries.length, setQueries, setTotal, setRegionStatusSummary]);

  // Track previous filtered queries length to detect data changes
  const prevFilteredLengthRef = useRef(filteredQueries.length);
  const prevRowsPerPageRef = useRef(rowsPerPage);

  // Reset page only when filtered data or rowsPerPage changes (not when user navigates pages)
  useEffect(() => {
    const maxPage = Math.max(0, Math.ceil(filteredQueries.length / rowsPerPage) - 1);
    const dataChanged = prevFilteredLengthRef.current !== filteredQueries.length || prevRowsPerPageRef.current !== rowsPerPage;

    // Only reset if current page is invalid AND data actually changed (not just user navigation)
    if (dataChanged && page > maxPage && filteredQueries.length > 0) {
      setPage(0);
    }

    // Update refs
    prevFilteredLengthRef.current = filteredQueries.length;
    prevRowsPerPageRef.current = rowsPerPage;
  }, [filteredQueries.length, rowsPerPage, page, setPage]); // Include page to check its value, but only reset on data changes

  return {
    filteredQueries,
    paginatedQueries,
  };
};
